const FORMAT_DATE_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
}

const FORMAT_TIME_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
}

const FORMAT_DATE_TIME_DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = {
  ...FORMAT_DATE_DEFAULT_OPTIONS,
  ...FORMAT_TIME_DEFAULT_OPTIONS,
}

const toDate = (value: string | Date): Date | undefined => {
  if (typeof value === "string" && value.length > 0) {
    return new Date(value)
  }
  if (value instanceof Date) {
    return value
  }
}

const isValidDate = (date: Date): date is Date => !isNaN(date.getTime())

/**
 * Composable for formatting dates and times using Intl.DateTimeFormat
 * Locale is not specified will therefore rely on the browsers default locale (and the users own system settings)
 * Returns standardized methods to format dates, times, and date-times according to the current locale, as well as a customizable formatting method for unique use cases.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 */
export const getDateTimeFormatters = () => {
  // Customizable date-time formatting method - use ONLY for unique use cases, the default methods below should cover most needs!
  const formatDateTimeCustom = (
    value: string | Date | null | undefined,
    options: Intl.DateTimeFormatOptions,
    fallbackValue: string | undefined = "-"
  ) => {
    if (!value) return fallbackValue

    const date = toDate(value)
    if (date && !isValidDate(date)) return fallbackValue

    return new Intl.DateTimeFormat(undefined, options).format(date)
  }

  // Standardized methods for date-time formatting - use this unless you have a unique use case
  const formatDateTime = (value: string | Date | null | undefined) => {
    return formatDateTimeCustom(
      value,
      FORMAT_DATE_TIME_DEFAULT_OPTIONS,
      "- -:-"
    )
  }

  // Standardized method for date formatting - use this unless you have a unique use case
  const formatDate = (value: string | Date | null | undefined) => {
    return formatDateTimeCustom(value, FORMAT_DATE_DEFAULT_OPTIONS, "-")
  }

  // Standardized method for time formatting - use this unless you have a unique use case
  const formatTime = (value: string | Date | null | undefined) => {
    return formatDateTimeCustom(value, FORMAT_TIME_DEFAULT_OPTIONS, "-:-")
  }

  return { formatDateTime, formatDate, formatTime, formatDateTimeCustom }
}

/*
 * @deprecated
 * Do not use this anymore. The equivalent is the formatDateTimeCustom function from getDateTimeFormatters()
 */
export const formatDate = (
  date: string | Date,
  locale: string,
  options: Intl.DateTimeFormatOptions
) => {
  if (locale === "en-US") {
    // We want to avoid AM/PM since we're using en-US in European countries where it's difficult to understand
    locale = "en-GB"
  }
  if (typeof date === "string") {
    date = new Date(date)
  }

  return new Intl.DateTimeFormat(locale, options).format(date)
}
